import { defineStore } from 'pinia'
import { HTTPError } from '@/util/http'
import type { ObjectiveLevel } from '../objectives/objective_levels'
import { getGenerateAccess, getGeneratedAssessments } from './api'
import type { GeneratedAssessmentTarget } from './generated_assessment_types'

const NOT_FOUND_STATUS = 404
const ANNUAL_TARGET_TYPE = 'goal'
const OBJECTIVE_TARGET_TYPE = 'objective'

function isNotFound(error: unknown): boolean {
  return error instanceof HTTPError && error.response.status === NOT_FOUND_STATUS
}

/** A saved page's generated assessments, keyed by saved page uuid, as the server groups them. */
export const useGeneratedAssessmentsStore = defineStore('generatedAssessments', {
  state: () => ({
    targetsBySavedPage: {} as Record<string, GeneratedAssessmentTarget[]>,
    generateAccessBySavedPage: {} as Record<string, boolean>
  }),

  getters: {
    // The server owns the grouping and the order (annual target first, then objectives by level, oldest
    // first within a group), so the client only indexes into what it was given.
    targetsFor(state) {
      return (savedPageUuid: string): GeneratedAssessmentTarget[] => state.targetsBySavedPage[savedPageUuid] ?? []
    },
    // Whether the page was fetched, not whether it has any: an empty answer and a 404 both store [].
    isLoadedFor(state) {
      return (savedPageUuid: string): boolean => Object.hasOwn(state.targetsBySavedPage, savedPageUuid)
    },
    annualTargetFor() {
      return (savedPageUuid: string): GeneratedAssessmentTarget | null =>
        this.targetsFor(savedPageUuid).find((target) => target.target_type === ANNUAL_TARGET_TYPE) ?? null
    },
    objectiveTargetFor() {
      return (savedPageUuid: string, level: ObjectiveLevel): GeneratedAssessmentTarget | null =>
        this.targetsFor(savedPageUuid).find(
          (target) => target.target_type === OBJECTIVE_TARGET_TYPE && target.target_level === level
        ) ?? null
    },
    // False until the server has said yes, so the entry point never appears and is then withdrawn.
    isGenerateAllowedFor(state) {
      return (savedPageUuid: string): boolean => state.generateAccessBySavedPage[savedPageUuid] === true
    }
  },

  actions: {
    /** A 404 is the endpoint's answer for every refusal as well as for a missing page, so it stores none. */
    async fetchGeneratedAssessments(savedPageUuid: string): Promise<GeneratedAssessmentTarget[]> {
      if (!savedPageUuid) {
        return []
      }
      try {
        const response = await getGeneratedAssessments(savedPageUuid)
        const targets = response?.targets ?? []
        this.targetsBySavedPage[savedPageUuid] = targets
        return targets
      } catch (error) {
        if (!isNotFound(error)) {
          throw error
        }
        this.targetsBySavedPage[savedPageUuid] = []
        return []
      }
    },

    /** Never rejects: a failure costs a hidden entry point, and the server re-checks on the generate call. */
    async fetchGenerateAccess(savedPageUuid: string): Promise<boolean> {
      if (!savedPageUuid) {
        return false
      }
      try {
        const response = await getGenerateAccess(savedPageUuid)
        this.generateAccessBySavedPage[savedPageUuid] = Boolean(response?.allowed)
      } catch {
        this.generateAccessBySavedPage[savedPageUuid] = false
      }
      return this.generateAccessBySavedPage[savedPageUuid]
    },

    // A group that empties is dropped, as the server never sends an empty one.
    removeGeneratedAssessment(savedPageUuid: string, uuid: string) {
      const targets = this.targetsBySavedPage[savedPageUuid]
      if (!targets) {
        return
      }
      this.targetsBySavedPage[savedPageUuid] = targets
        .map((target) => ({ ...target, assessments: (target.assessments || []).filter((assessment) => assessment.uuid !== uuid) }))
        .filter((target) => target.assessments.length > 0)
    }
  }
})
